import React, { useEffect, useState } from "react";
import { Card, Typography, Form, Input, Button, Alert } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import {
  loginWithPassword,
  getUserFromToken,
  clearTokens,
  isAuthenticated,
} from "@/services/keycloak";

const { Title, Text } = Typography;

// Charte Filmika
const BRAND = {
  bg: "#0E2A1F",
  green: "#265A50",
  greenTint: "#53A393",
  orange: "#f38218",
  orangeDark: "#d46e0f",
};

// Rôles autorisés à accéder au backoffice. VIEWER (abonnés mobiles) est exclu.
const ALLOWED_BACKOFFICE_ROLES = ["ADMIN", "EDITOR", "AUTHOR", "REVIEWER", "SUPER_ADMIN"];

/**
 * Formulaire de connexion personnalisé (charte Filmika) — sans redirection vers
 * la page Keycloak. Utilise le Direct Access Grant (username/password) via le
 * client public backoffice-spa. Rejette les comptes sans rôle backoffice (VIEWER)
 * avec un message d'erreur clair.
 */
export const LoginPageOAuth2: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si déjà connecté -> dashboard. Message d'erreur éventuel passé en query.
  useEffect(() => {
    const authError = searchParams.get("auth_error");
    if (authError) setError(decodeURIComponent(authError));
    (async () => {
      if (await isAuthenticated()) navigate("/", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      await loginWithPassword(values.username.trim(), values.password);

      // Garde de rôle : rejeter les comptes sans rôle backoffice (ex : VIEWER).
      const user = getUserFromToken();
      const authorized = (user?.roles || []).some((r) =>
        ALLOWED_BACKOFFICE_ROLES.includes(String(r).toUpperCase())
      );
      if (!authorized) {
        clearTokens();
        setError("Connexion échouée : votre compte n'a pas les droits pour accéder au backoffice.");
        setLoading(false);
        return;
      }

      navigate("/", { replace: true });
    } catch (e: any) {
      setError(e?.message || "Connexion échouée. Vérifiez vos identifiants.");
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${BRAND.bg} 0%, ${BRAND.green} 100%)`,
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 400,
          maxWidth: "100%",
          borderRadius: 14,
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          border: "none",
        }}
        styles={{ body: { padding: "36px 32px" } }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src="/filmika-logo.png"
            alt="Filmika"
            style={{ height: 56, marginBottom: 16, objectFit: "contain" }}
          />
          <Title level={4} style={{ margin: 0, color: BRAND.bg }}>
            Backoffice
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Connectez-vous pour continuer
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 20, textAlign: "left" }}
          />
        )}

        <Form layout="vertical" onFinish={onFinish} requiredMark={false} disabled={loading}>
          <Form.Item
            label="Nom d'utilisateur"
            name="username"
            rules={[{ required: true, message: "Veuillez saisir votre nom d'utilisateur" }]}
          >
            <Input
              size="large"
              prefix={<UserOutlined style={{ color: BRAND.greenTint }} />}
              placeholder="nom d'utilisateur ou email"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label="Mot de passe"
            name="password"
            rules={[{ required: true, message: "Veuillez saisir votre mot de passe" }]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined style={{ color: BRAND.greenTint }} />}
              placeholder="••••••••"
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
            <Button
              htmlType="submit"
              size="large"
              icon={<LoginOutlined />}
              loading={loading}
              block
              style={{
                background: BRAND.orange,
                borderColor: BRAND.orange,
                color: "#fff",
                fontWeight: 600,
                height: 46,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = BRAND.orangeDark)}
              onMouseLeave={(e) => (e.currentTarget.style.background = BRAND.orange)}
            >
              Se connecter
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            🔐 Accès réservé au personnel autorisé
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default LoginPageOAuth2;
